import { useCallback, useEffect } from 'react';
import { Loader2, Package, Layers, Boxes, ArrowLeftRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalHeader,
  ResponsiveModalBody,
  ResponsiveModalFooter,
  ResponsiveModalTitle,
  ResponsiveModalDescription,
} from '@/components/ui/responsive-modal';
import { usePOSImport } from '@/hooks/usePOSImport';
import { useLocationStore } from '@/store/location';
import { getPOSSource } from '@/lib/import/sources';
import { downloadBlob, generateErrorReport } from '@/lib/import';
import { ImportFileUpload } from './ImportFileUpload';
import { OutletMappingStep } from './OutletMappingStep';
import { ImportProgress } from './ImportProgress';
import { ImportResults } from './ImportResults';

interface POSImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The POS source id, e.g. 'lightspeed' */
  sourceId: string;
}

export function POSImportDialog({
  open,
  onOpenChange,
  sourceId,
}: POSImportDialogProps) {
  const { locations } = useLocationStore();
  const source = getPOSSource(sourceId);

  const {
    step,
    isLoading,
    error,
    detectedOutlets,
    outletMapping,
    parsedData,
    progress,
    results,
    setSourceId,
    uploadFile,
    setOutletMapping,
    confirmMapping,
    startImport,
    goBack,
    reset,
  } = usePOSImport();

  // Set source id when dialog opens
  useEffect(() => {
    if (open && sourceId) {
      setSourceId(sourceId);
    }
  }, [open, sourceId, setSourceId]);

  // Reset when dialog closes
  useEffect(() => {
    if (!open) {
      const timer = setTimeout(reset, 300);
      return () => clearTimeout(timer);
    }
  }, [open, reset]);

  const handleFileSelect = useCallback((file: File) => {
    uploadFile(file);
  }, [uploadFile]);

  const handleDownloadTemplate = useCallback(() => {
    // No template for POS imports -- this is a no-op
  }, []);

  const handleClose = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  const handleDownloadErrors = useCallback(() => {
    if (!results) return;
    const allErrors = [
      ...results.products.errors.map(e => ({ ...e, sheet: 'Products', field: 'product' })),
      ...results.categories.errors.map(e => ({ ...e, sheet: 'Categories', field: 'category' })),
    ];
    const blob = generateErrorReport(allErrors);
    downloadBlob(blob, `${sourceId}-import-errors.xlsx`);
  }, [results, sourceId]);

  const sourceName = source?.name ?? sourceId;
  const acceptedTypes = source?.acceptedFileTypes.join(',') ?? '.csv';

  const getStepTitle = () => {
    switch (step) {
      case 'upload':
        return `Import from ${sourceName}`;
      case 'outlet-mapping':
        return 'Map Outlets to Locations';
      case 'preview':
        return 'Review Import Data';
      case 'importing':
        return 'Importing...';
      case 'results':
        return 'Import Complete';
    }
  };

  const getStepDescription = () => {
    switch (step) {
      case 'upload':
        return `Upload your ${sourceName} product export file (${acceptedTypes}).`;
      case 'outlet-mapping':
        return 'Map each outlet from your export to a BlazePOS location for inventory data.';
      case 'preview':
        return 'Review the products that will be imported from your file.';
      case 'importing':
        return 'Please wait while your products are being imported.';
      case 'results':
        return 'Your import has been processed. Review the results below.';
    }
  };

  const canProceedFromPreview = parsedData && parsedData.products.length > 0;

  return (
    <ResponsiveModal open={open} onOpenChange={onOpenChange}>
      <ResponsiveModalContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <ResponsiveModalHeader>
          <ResponsiveModalTitle>{getStepTitle()}</ResponsiveModalTitle>
          <ResponsiveModalDescription>{getStepDescription()}</ResponsiveModalDescription>
        </ResponsiveModalHeader>

        <ResponsiveModalBody className="flex-1 overflow-y-auto">
          {step === 'upload' && (
            <ImportFileUpload
              onFileSelect={handleFileSelect}
              onDownloadTemplate={handleDownloadTemplate}
              isLoading={isLoading}
              error={error}
            />
          )}

          {step === 'outlet-mapping' && (
            <OutletMappingStep
              detectedOutlets={detectedOutlets}
              outletMapping={outletMapping}
              onMappingChange={setOutletMapping}
              locations={locations}
            />
          )}

          {step === 'preview' && parsedData && (
            <POSImportPreview data={parsedData} />
          )}

          {step === 'importing' && progress && (
            <ImportProgress progress={progress} />
          )}

          {step === 'results' && results && (
            <ImportResults
              results={results}
              onDownloadErrors={handleDownloadErrors}
              onClose={handleClose}
            />
          )}
        </ResponsiveModalBody>

        {/* Footer with navigation buttons */}
        {step !== 'results' && step !== 'importing' && (
          <ResponsiveModalFooter className="flex justify-between border-t pt-4">
            <div>
              {(step === 'preview' || step === 'outlet-mapping') && (
                <Button variant="outline" onClick={goBack}>
                  Back
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              {step === 'outlet-mapping' && (
                <Button onClick={confirmMapping} disabled={isLoading}>
                  {isLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Continue
                </Button>
              )}
              {step === 'preview' && (
                <Button
                  onClick={startImport}
                  disabled={!canProceedFromPreview || isLoading}
                >
                  {isLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Import {parsedData?.summary.totalProducts ?? 0} Products
                </Button>
              )}
            </div>
          </ResponsiveModalFooter>
        )}
      </ResponsiveModalContent>
    </ResponsiveModal>
  );
}

// ---------------------------------------------------------------------------
// Preview component for POS import data
// ---------------------------------------------------------------------------

function POSImportPreview({ data }: { data: import('@/lib/import/sources').POSImportData }) {
  const { summary, parseWarnings, categories, products } = data;

  return (
    <div className="space-y-6">
      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="p-4 border rounded-lg space-y-1">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Package className="h-4 w-4" />
            <span className="text-xs font-medium">Total Products</span>
          </div>
          <p className="text-2xl font-bold">{summary.totalProducts}</p>
        </div>
        <div className="p-4 border rounded-lg space-y-1">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Layers className="h-4 w-4" />
            <span className="text-xs font-medium">With Variants</span>
          </div>
          <p className="text-2xl font-bold">
            {summary.variantProducts + summary.compositeVariantProducts}
          </p>
          <p className="text-xs text-muted-foreground">
            {summary.totalVariants} total variant{summary.totalVariants !== 1 ? 's' : ''}
          </p>
        </div>
        <div className="p-4 border rounded-lg space-y-1">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Boxes className="h-4 w-4" />
            <span className="text-xs font-medium">Composites</span>
          </div>
          <p className="text-2xl font-bold">
            {summary.compositeProducts + summary.compositeVariantProducts}
          </p>
          <p className="text-xs text-muted-foreground">
            {summary.totalComponents} component{summary.totalComponents !== 1 ? 's' : ''}
          </p>
        </div>
        <div className="p-4 border rounded-lg space-y-1">
          <div className="flex items-center gap-2 text-muted-foreground">
            <ArrowLeftRight className="h-4 w-4" />
            <span className="text-xs font-medium">Categories</span>
          </div>
          <p className="text-2xl font-bold">{categories.length}</p>
        </div>
      </div>

      {/* Type breakdown */}
      <div className="border rounded-lg overflow-hidden">
        <div className="px-4 py-3 bg-muted/50 border-b">
          <h4 className="text-sm font-medium">Product Breakdown</h4>
        </div>
        <div className="divide-y">
          <div className="flex items-center justify-between px-4 py-2 text-sm">
            <span>Standard products</span>
            <span className="font-medium">{summary.simpleProducts}</span>
          </div>
          <div className="flex items-center justify-between px-4 py-2 text-sm">
            <span>Variant products</span>
            <span className="font-medium">{summary.variantProducts}</span>
          </div>
          <div className="flex items-center justify-between px-4 py-2 text-sm">
            <span>Composite products (BOM)</span>
            <span className="font-medium">{summary.compositeProducts}</span>
          </div>
          <div className="flex items-center justify-between px-4 py-2 text-sm">
            <span>Composite + variant products</span>
            <span className="font-medium">{summary.compositeVariantProducts}</span>
          </div>
        </div>
      </div>

      {/* Product list preview (first 20) */}
      <div className="border rounded-lg overflow-hidden">
        <div className="px-4 py-3 bg-muted/50 border-b">
          <h4 className="text-sm font-medium">
            Products Preview
            {products.length > 20 && (
              <span className="text-muted-foreground font-normal">
                {' '}(showing first 20 of {products.length})
              </span>
            )}
          </h4>
        </div>
        <div className="divide-y max-h-[300px] overflow-y-auto">
          {products.slice(0, 20).map((product, idx) => (
            <div key={idx} className="flex items-center justify-between px-4 py-2 text-sm">
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{product.name}</p>
                <p className="text-xs text-muted-foreground">
                  SKU: {product.sku || 'N/A'}
                  {product.category && ` | ${product.category}`}
                  {product.product_type !== 'item' && (
                    <span className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-muted">
                      {product.product_type}
                    </span>
                  )}
                </p>
              </div>
              <div className="text-right ml-4">
                <p className="font-medium">${product.price.toFixed(2)}</p>
                <p className="text-xs text-muted-foreground">
                  {product.is_sellable ? 'Sellable' : 'Raw Goods'}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Parse warnings */}
      {parseWarnings.length > 0 && (
        <div className="border border-yellow-500/20 bg-yellow-500/5 rounded-lg p-4 space-y-2">
          <h4 className="text-sm font-medium text-yellow-700 dark:text-yellow-400">
            Warnings ({parseWarnings.length})
          </h4>
          <div className="space-y-1 max-h-[150px] overflow-y-auto">
            {parseWarnings.map((w, idx) => (
              <p key={idx} className="text-xs text-muted-foreground">
                {w.row ? `Row ${w.row}: ` : ''}{w.message}
              </p>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
